import { listsUnder } from '../api/catalog.generated'
import type { ModuleNode } from '../api/types'
import { tOr } from '../i18n'

/**
 * The left-hand navigation, from what the instance actually loaded.
 *
 * `modules.yml` decides which modules a site runs — a Simple-mode project has
 * six, not twenty-one — so the menu is built from `/session/me`, never from a
 * list in this repository. A module added next month appears here with no
 * change to the front end.
 *
 * Some modules have screens somebody designed. The rest get a generated
 * read-only screen off the OpenAPI document, which is worth much more than a
 * card that says "coming soon" and shows nothing: the records are visible, the
 * filters the API declares work, and the module's own help topic opens beside
 * them.
 */

export type NavEntry = {
  code: string
  label: string
  toolCode: string
  /** Where the browser goes. */
  path: string
  /** The registered API route this screen asks the help panel about. */
  helpRoute: string
  built: boolean
}

/** Modules with a screen of their own, and the API route their help hangs on. */
const BUILT: Record<string, { slug: string; helpRoute: string }> = {
  'CM-PUN': { slug: 'deficiency', helpRoute: '/v1/deficiencies/' },
  'CM-RFI': { slug: 'rfi', helpRoute: '/rfi/rfis' },
  // Change management. The generated table listed change events and could do
  // nothing else: a change is a chain of a price, a directive and a signed
  // order, and none of those were reachable from the browser.
  'CM-CHG': { slug: 'changes', helpRoute: '/changes/events' },
  // The day register and one day's running list. The generated table showed
  // the days and could open none of them: entries had no read of their own,
  // so a day's contents were unreachable from the browser.
  'CM-DLY': { slug: 'dailylog', helpRoute: '/dailylog/days' },
  // The Daily Construction Report. The generated table listed the reports
  // and the schedule imports and could open, start or print none of them, so
  // nothing the module is for could be done from the browser.
  'CM-FRP': { slug: 'fieldreport', helpRoute: '/fieldreport/reports' },
  // The master schedule. The generated table listed the issues and could not
  // import one, which is the only way an issue gets there, and could not open
  // the activities, which are what anybody comes to read.
  'CM-SCH': { slug: 'schedule', helpRoute: '/schedule/activities' },
  // Photos and media. The generated table listed the records and showed no
  // photograph, took no upload and opened nothing, which is everything the
  // tool is for.
  'CM-PHO': { slug: 'photos', helpRoute: '/photos/media' },
  'CM-MTG': { slug: 'meetings', helpRoute: '/meetings/occurrences' },
  // The set and the book. Their generated tables listed the same rows and
  // opened none of them, which is the whole difference: a sheet number is a
  // link to the drawing now, and a section number opens the book at it.
  'CM-DWG': { slug: 'drawings', helpRoute: '/drawings/sheets' },
  'CM-SPC': { slug: 'specs', helpRoute: '/specs/sections' },
  // The canvas's own page. Markup is drawn inside the documents it marks up,
  // so the generated table could reach none of it -- every list but the stamp
  // palette needs a document named -- and the page said "not built yet" over
  // a table of stamps. This one lists every markup and comment on the job and
  // where it is, and keeps the palette.
  'CM-MKP': { slug: 'markup', helpRoute: '/markup/log' },
  // Models: a generated table could list the versions and could neither take
  // an upload nor open one, which are the two things the module is for.
  'CM-MDL': { slug: 'models', helpRoute: '/models' },
  // The scope backbone. The generated table listed the divisions and could
  // neither divide one between contractors nor show what a scope had been
  // before, which are the two things the module is for.
  'CM-SCP': { slug: 'scope', helpRoute: '/scope/divisions' },
  // Presence, which the generated table could only ever show as a list of
  // report rows: the two figures a superintendent opens this for — who is here
  // now and how that compares with last week — are not rows at all.
  'CM-WFA': { slug: 'workforce', helpRoute: '/workforce/overview' },
  // The submittal register, detail and review. The generated table it replaces
  // had nineteen columns named after the schema and four of them bare
  // identifiers, and could write nothing at all: twenty of the module's
  // twenty-three operations were unreachable, create and submit and review
  // among them. A log that can show a submittal and not submit one is a log
  // people keep in a spreadsheet beside it.
  'CM-SUB': { slug: 'submittals', helpRoute: '/submittals' },
  // The correspondence register, an item and the matter it belongs to. The
  // generated table showed the register under a "Not built yet" banner and
  // could raise, issue, answer or close nothing, which is all the module is for.
  'CM-COR': { slug: 'correspondence', helpRoute: '/correspondence/items' },
  // The window onto SiteFocus: QAQC-LOTO. A generated table could not show
  // this one at all: what matters on it is the state of the answer — old,
  // unavailable, part-answered, not recorded — and a generated table has no
  // vocabulary for any of that. It would have drawn a failed read as an empty
  // list, which is the one thing this screen may never do.
  'CM-INS': { slug: 'qaqc-loto', helpRoute: '/qaqc-loto/summary' },
  // The project directory. The generated table was the directory's change
  // history, which answers "what changed" and not the question the screen is
  // opened for: who is on this job, for whom, and who can add people. The
  // history stays one link away from the new screen.
  'CM-TEN': { slug: 'directory', helpRoute: '/directory/projects/{project_id}/members' },
  // The installed equipment register. A generated table could list the units
  // and could neither register one nor attach its manual, and it had no way to
  // say what a unit is still missing for handover, which is the question the
  // register is opened for.
  'CM-EQP': { slug: 'equipment', helpRoute: '/equipment/assets' },
  // The document repository had no module at all before this screen, so there
  // was no generated table to replace: folders, versions and private folders
  // are the module, and a list of rows could have shown none of them.
  'CM-DOC': { slug: 'repository', helpRoute: '/repository/documents' },
  // Feedback's own screens: the reports a person filed, and the board. Listed
  // here so the menu can offer them by the one choice below; while that choice
  // is off, `HIDDEN` keeps the module out of the menu and this line is unused.
  'CM-FBK': { slug: 'feedback', helpRoute: '/feedback/reports' },
}

/**
 * Whether Feedback is listed in the module menu.
 *
 * Off: the way in is the Report a problem button on every screen, whose window
 * links to "Your reports" and, for triage, the "Feedback board". On: Feedback is
 * also a menu entry opening "Your reports". Either way the same screens answer
 * at the same addresses and the server decides who reads what; this changes
 * only whether the menu offers the way in. The owner's choice, not ours.
 */
export const FEEDBACK_IN_MENU = false

/** Modules whose screens are the platform's own, not a module page. */
const HIDDEN = new Set([
  // The example module is the module contract's own fixture. It ships so a
  // new module has something to copy, and a site has no use for its screen.
  'CM-XMP',
  // Feedback is reachable from the button on every screen; a second entry in
  // the menu would be a second place to file the same report.
  ...(FEEDBACK_IN_MENU ? [] : ['CM-FBK']),
])

/**
 * Modules that keep their screen and their address but are offered nowhere in
 * the app: no menu, no job card, no link from a row. Putting one back is
 * deleting its line. Not `HIDDEN`, which also takes the page's tab name.
 *
 * The markup canvas is kept off at the owner's word: its page still works at
 * its address, and markup is still drawn inside the drawings and specs.
 */
export const OFF_MENU: ReadonlySet<string> = new Set(['CM-MKP'])

/** The entries the app offers as a way in: every entry not kept off. */
export function menuEntries(modules: ModuleNode[], projectId: string): NavEntry[] {
  return navEntries(modules, projectId).filter((entry) => !OFF_MENU.has(entry.code))
}

export function navEntries(modules: ModuleNode[], projectId: string): NavEntry[] {
  const entries: NavEntry[] = []
  for (const module of modules) {
    if (HIDDEN.has(module.code)) continue
    const label = tOr(`module.${module.code}`, module.name)
    const built = BUILT[module.code]
    if (built) {
      entries.push({
        code: module.code,
        label,
        toolCode: module.tool_code,
        path: `/p/${projectId}/${built.slug}`,
        helpRoute: built.helpRoute,
        built: true,
      })
      continue
    }
    const lists = listsUnder(module.route_prefix)
    if (lists.length === 0) continue
    entries.push({
      code: module.code,
      label,
      toolCode: module.tool_code,
      path: `/p/${projectId}/m/${module.code}`,
      helpRoute: lists[0]?.path ?? module.route_prefix,
      built: false,
    })
  }
  // Built screens first, then the generated ones alphabetically: a foreman
  // looking for the punch list should not have to read past nine modules that
  // are only lists.
  entries.sort((a, b) =>
    a.built === b.built ? a.label.localeCompare(b.label) : a.built ? -1 : 1,
  )
  // The specification book sits directly under the drawings, at the owner's
  // word: the two are read together, and alphabetical order put a dozen
  // modules between them.
  const specs = entries.findIndex((entry) => entry.code === 'CM-SPC')
  if (specs !== -1 && entries.some((entry) => entry.code === 'CM-DWG')) {
    const [entry] = entries.splice(specs, 1)
    const drawings = entries.findIndex((other) => other.code === 'CM-DWG')
    entries.splice(drawings + 1, 0, entry as NavEntry)
  }
  return entries
}

/**
 * The documents search, which belongs to no module and to two.
 *
 * It is not in :func:`navEntries` because that list is one entry per module
 * read off `/session/me`, and this screen searches the drawing set and the
 * specification book through two routes. Putting it under one of them would
 * bury the other; giving it two entries would be one screen listed twice. So
 * it sits above the module list, beside Home, and appears only where the
 * instance actually runs one of the two — a Simple-mode project with neither
 * gets no entry rather than a screen with an empty picker.
 */
export function documentsEntry(modules: ModuleNode[], projectId: string): NavEntry | null {
  // Drawings first where both are present, because that is the source the
  // screen opens on: the entry has one tool code and one help route, and both
  // have to name the search the reader actually lands in.
  const opening =
    modules.find((module) => module.code === 'CM-DWG') ??
    modules.find((module) => module.code === 'CM-SPC')
  if (!opening) return null
  return {
    code: 'documents',
    label: tOr('documents.title', 'Search the documents'),
    toolCode: opening.tool_code,
    path: `/p/${projectId}/documents`,
    helpRoute: opening.code === 'CM-DWG' ? '/drawings/search' : '/specs/search',
    built: true,
  }
}

export function moduleFor(modules: ModuleNode[], code: string): ModuleNode | undefined {
  return modules.find((module) => module.code === code)
}

/** One line of the tools menu on the bar. */
export type ToolLink = {
  code: string
  label: string
  path: string
  locked: boolean
  /** A module with no screen of its own yet, shown as its generated list. */
  preview?: boolean
}

export type ToolGroup = { key: string; links: ToolLink[] }

/**
 * The tools menu's grouping, by what a person is doing rather than by how the
 * modules happen to be built.
 *
 * The left-hand list stays the order the instance gives it; this is the second
 * way in, from the bar, and it is grouped because a menu of twenty names in one
 * column is read top to bottom every time. A module this table does not name
 * still appears, under "Other tools", so a module added next month is never
 * missing from the bar.
 */
const TOOL_GROUPS: { key: string; codes: string[] }[] = [
  { key: 'core', codes: ['home', 'documents', 'CM-TEN', 'devices'] },
  {
    key: 'project',
    codes: [
      'CM-RFI',
      'CM-SUB',
      'CM-DWG',
      'CM-MDL',
      'CM-SPC',
      'CM-DOC',
      'CM-MKP',
      'CM-MTG',
      'CM-COR',
      'CM-SCH',
    ],
  },
  { key: 'field', codes: ['CM-DLY', 'CM-FRP', 'CM-PHO', 'CM-PUN', 'CM-INS', 'CM-WFA', 'CM-EQP'] },
  { key: 'cost', codes: ['CM-CHG'] },
]

export function toolGroups(links: ToolLink[]): ToolGroup[] {
  const placed = new Set<string>()
  const groups: ToolGroup[] = []
  for (const group of TOOL_GROUPS) {
    const inGroup = group.codes
      .map((code) => links.find((link) => link.code === code))
      .filter((link): link is ToolLink => link !== undefined)
    inGroup.forEach((link) => placed.add(link.code))
    if (inGroup.length > 0) groups.push({ key: group.key, links: inGroup })
  }
  const rest = links.filter((link) => !placed.has(link.code))
  if (rest.length > 0) groups.push({ key: 'other', links: rest })
  return groups
}
